import {Component, input, OnInit} from '@angular/core';
import {Button} from 'primeng/button';
import {DynamicDialogConfig, DynamicDialogRef} from 'primeng/dynamicdialog';
import {FormControl, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {Tag} from 'primeng/tag';
import {AutoComplete} from 'primeng/autocomplete';
import {BasicUserDetails, ClcUnitDetails} from '../../../../api';
import {TaAppStore} from '../../../../state/clcta-store';
import {ToggleSwitch} from 'primeng/toggleswitch';
import {InputText} from 'primeng/inputtext';
import {DatePicker} from 'primeng/datepicker';
import {IconBtnComponent} from '../../icon-b/icon-btn.component';
import {toLocalISOString} from '../../../../util/date.utils';


@Component({
  selector: 'manage-unit-modal',
  imports: [
    Button,
    FormsModule,
    ToggleSwitch,
    InputText,
    Tag,
    AutoComplete,
    ReactiveFormsModule,
    DatePicker,
    IconBtnComponent,
  ],
  templateUrl: './manage-unit-modal.component.html',
  styleUrl: './manage-unit-modal.component.scss'
})
export class ManageUnitModalComponent implements OnInit {
  modalData = input<ClcUnitDetails>();
  availableManagers = input<BasicUserDetails[]>([]);
  availableApprovers = input<BasicUserDetails[]>([]);

  localData: ClcUnitDetails = {
    name: '',
    code: '',
    active: true,
    managers: [],
    actingManager: undefined,
    actingTo: null,
    approvers: [],
  };

  // p-datepicker needs a real Date, but the API carries actingTo as a string - see toActingTo().
  actingToDate: Date | null = null;
  today: Date = new Date();

  filteredManagers: BasicUserDetails[] = [];
  filteredActingManagers: BasicUserDetails[] = [];
  filteredApprovers: BasicUserDetails[] = [];

  managerControl = new FormControl<BasicUserDetails | null>(null);
  actingManagerControl = new FormControl<BasicUserDetails | null>(null);
  approverControl = new FormControl<BasicUserDetails | null>(null);

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    public store: TaAppStore
  ) {}

  ngOnInit() {
    const data = this.modalData();
    if (!!data) {
      this.localData = {
        ...data,
        managers: data.managers?.map(manager => ({...manager})) ?? [],
        actingManager: !!data.actingManager ? {...data.actingManager} : undefined,
        approvers: data.approvers?.map(approver => ({...approver})) ?? [],
      };
      this.actingToDate = !!data.actingTo ? new Date(data.actingTo) : null;
      this.managerControl.setValue(this.manager ?? null);
      this.actingManagerControl.setValue(this.localData.actingManager ?? null);
    }
  }

  /**
   * Code and name identify the unit, and everything else on the form hangs off it, so the rest of
   * the fields stay disabled until both are filled. An existing unit always arrives with both.
   */
  get hasUnitDetails(): boolean {
    return !!this.localData.code?.trim() && !!this.localData.name?.trim();
  }

  /**
   * An acting manager stands in for the permanent one, so there is nothing to stand in for until a
   * unit manager is set. Gates the whole acting-manager block, not just the picker.
   */
  get hasManager(): boolean {
    return !!this.manager;
  }

  /** A unit is only saveable once it has a code and a name. */
  get canSave(): boolean {
    return this.hasUnitDetails;
  }

  /**
   * The API carries managers as a list, but the server only ever holds one per unit, so the form
   * edits a single manager and writes it back as a list of one.
   */
  get manager(): BasicUserDetails | undefined {
    return this.localData.managers?.[0];
  }

  setManager(): void {
    this.localData.managers = !!this.managerControl.value ? [{...this.managerControl.value}] : [];
  }

  /**
   * Clears the acting manager too - the acting fields disable without a manager, so leaving one
   * behind would strand it where it can no longer be removed.
   */
  clearManager(): void {
    this.localData.managers = [];
    this.managerControl.reset();
    this.clearActingManager();
  }

  setActingManager(): void {
    this.localData.actingManager = !!this.actingManagerControl.value ? {...this.actingManagerControl.value} : undefined;
  }

  clearActingManager(): void {
    this.localData.actingManager = undefined;
    this.actingToDate = null;
    this.actingManagerControl.reset();
  }

  addApprover(): void {
    const approver = this.approverControl.value;
    if (!approver) {
      return;
    }
    if (!!this.localData.approvers) {
      this.localData.approvers.push({...approver});
    } else {
      this.localData.approvers = [{...approver}];
    }
    this.approverControl.reset();
  }

  removeApprover(index: number): void {
    if (!!this.localData.approvers) {
      this.localData.approvers.splice(index, 1);
    }
  }

  /** The acting manager stands in for the manager, so nobody can hold both roles at once. */
  filterManagers(event: any): void {
    this.filteredManagers = this.matchUsers(this.availableManagers(), event.query, [this.localData.actingManager?.id]);
  }

  filterActingManagers(event: any): void {
    this.filteredActingManagers = this.matchUsers(this.availableManagers(), event.query, [this.manager?.id]);
  }

  filterApprovers(event: any): void {
    const existingIds = (this.localData.approvers ?? []).map(approver => approver.id);
    this.filteredApprovers = this.matchUsers(this.availableApprovers(), event.query, existingIds);
  }

  private matchUsers(users: BasicUserDetails[], query: string, excludedIds: (string | null | undefined)[]): BasicUserDetails[] {
    return users.filter(user =>
      user.name?.toLowerCase().includes(query.toLowerCase()) &&
      !excludedIds.includes(user.id)
    );
  }

  private toActingTo(): string | null {
    if (!this.localData.actingManager || !this.actingToDate) {
      return null;
    }
    return toLocalISOString(this.actingToDate);
  }

  saveDialog(): void {
    this.ref.close({...this.localData, actingTo: this.toActingTo()});
  }

  closeDialog(data?: ClcUnitDetails): void {
    this.ref.close(data);
  }
}
